function createPlayer(card) {
  const video = document.createElement('video');
  const source = document.createElement('source');
  video.controls = true;
  video.playsInline = true;
  video.preload = 'metadata';
  source.src = card.dataset.videoUrl;
  source.type = card.dataset.videoType || 'video/mp4';
  video.append(source);
  return video;
}

const craftedInTibetControllers = new WeakMap();

function initCraftedInTibet(root = document) {
  const strips = root.matches?.('[data-pk-crafted-strip]') ? [root] : root.querySelectorAll('[data-pk-crafted-strip]');
  strips.forEach((strip) => {
    if (strip.dataset.craftReady === 'true') return;
    strip.dataset.craftReady = 'true';

    const items = [...strip.querySelectorAll('[data-craft-item]')];
    const cards = [...strip.querySelectorAll('[data-craft-card]')];
    const viewport = strip.querySelector('[data-craft-viewport]');
    const track = strip.querySelector('[data-craft-track]');
    const semanticTrack = strip.querySelector('[data-craft-semantic-track]');
    const visualTrack = strip.querySelector('[data-craft-visual-track]');
    const dialog = strip.querySelector('[data-craft-dialog]');
    const player = strip.querySelector('[data-craft-player]');
    const status = strip.querySelector('[data-craft-status]');
    const dialogTitle = strip.querySelector('[data-craft-dialog-title]');
    const dialogDescription = strip.querySelector('[data-craft-dialog-description]');
    const dialogLink = strip.querySelector('[data-craft-dialog-link]');
    const closeButton = strip.querySelector('[data-craft-close]');
    const previous = strip.querySelector('[data-craft-prev]');
    const toggle = strip.querySelector('[data-craft-toggle]');
    const next = strip.querySelector('[data-craft-next]');
    if (!items.length || !cards.length || !viewport || !track || !semanticTrack || !visualTrack || !dialog || !player || !dialogLink) return;

    const state = {
      hovered: false,
      focused: false,
      touching: false,
      dialogOpen: false,
      userPaused: false,
      intersecting: !('IntersectionObserver' in window),
      origin: null,
      frame: null,
      resumeTimer: null,
      lastTimestamp: null,
      loopWidth: 0,
      observer: null,
      savedOverflow: null,
      backdropPointerStarted: false,
      backdropPointerCompleted: false
    };
    const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldStop = () => state.hovered || state.focused || state.touching || state.dialogOpen || state.userPaused || !state.intersecting || document.hidden || reduceMotion();
    const pixelsPerSecond = () => Number(strip.dataset.speed) || 18;
    const clearResumeTimer = () => {
      if (state.resumeTimer !== null) window.clearTimeout(state.resumeTimer);
      state.resumeTimer = null;
    };
    const stopAnimation = () => {
      if (state.frame !== null) cancelAnimationFrame(state.frame);
      state.frame = null;
      state.lastTimestamp = null;
    };
    const measureLoop = () => {
      const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      state.loopWidth = semanticTrack.getBoundingClientRect().width + gap;
    };
    const normalizeScroll = () => {
      if (state.loopWidth <= 0) return;
      while (viewport.scrollLeft >= state.loopWidth) viewport.scrollLeft -= state.loopWidth;
      while (viewport.scrollLeft < 0) viewport.scrollLeft += state.loopWidth;
    };
    const animate = (timestamp) => {
      if (shouldStop()) {
        stopAnimation();
        return;
      }
      if (state.lastTimestamp !== null && state.loopWidth > 0) {
        viewport.scrollLeft += pixelsPerSecond() * (timestamp - state.lastTimestamp) / 1000;
        normalizeScroll();
      }
      state.lastTimestamp = timestamp;
      state.frame = requestAnimationFrame(animate);
    };
    const startAnimation = () => {
      clearResumeTimer();
      if (state.frame !== null || shouldStop() || items.length < 2) return;
      state.lastTimestamp = performance.now();
      state.frame = requestAnimationFrame(animate);
    };
    const resumeAfter = (delay = 350) => {
      clearResumeTimer();
      state.resumeTimer = window.setTimeout(() => {
        state.resumeTimer = null;
        normalizeScroll();
        startAnimation();
      }, delay);
    };
    const stepBy = (direction) => {
      stopAnimation();
      clearResumeTimer();
      measureLoop();
      const gap = Number.parseFloat(getComputedStyle(semanticTrack).columnGap || getComputedStyle(semanticTrack).gap) || 0;
      const amount = (items[0]?.getBoundingClientRect().width || 0) + gap;
      if (direction < 0 && viewport.scrollLeft < amount && state.loopWidth > 0) viewport.scrollLeft += state.loopWidth;
      viewport.scrollBy({ left: direction * amount, behavior: reduceMotion() ? 'auto' : 'smooth' });
      resumeAfter(reduceMotion() ? 0 : 350);
    };
    const setStatus = (message = '') => {
      status.textContent = message;
      status.hidden = !message;
    };
    const saveAndLockOverflow = () => {
      if (state.savedOverflow) return;
      state.savedOverflow = {
        html: document.documentElement.style.overflow,
        body: document.body.style.overflow
      };
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    };
    const restoreOverflow = () => {
      if (!state.savedOverflow) return;
      document.documentElement.style.overflow = state.savedOverflow.html;
      document.body.style.overflow = state.savedOverflow.body;
      state.savedOverflow = null;
    };
    const openCard = (card) => {
      state.origin = card;
      state.dialogOpen = true;
      stopAnimation();
      clearResumeTimer();
      player.replaceChildren();
      setStatus('Loading video…');
      const video = createPlayer(card);
      const mediaReadyHandler = () => setStatus();
      const mediaFailureHandler = () => {
        if (state.dialogOpen && player.contains(video)) setStatus('Video could not play. Close and try again.');
      };
      video.addEventListener('loadedmetadata', mediaReadyHandler, { once: true });
      video.addEventListener('canplay', mediaReadyHandler, { once: true });
      video.addEventListener('error', mediaFailureHandler, { once: true });
      player.append(video);
      dialogTitle.textContent = card.dataset.videoTitle || '';
      dialogDescription.textContent = card.dataset.videoDescription || '';
      dialogLink.href = card.dataset.craftProductUrl;
      saveAndLockOverflow();
      dialog.showModal();
      video.play().catch((error) => {
        if (error && state.dialogOpen && player.contains(video)) setStatus('Video could not play. Close and try again.');
      });
    };
    const closeDialog = (restart = true) => {
      const video = player.querySelector('video');
      if (video) video.pause();
      player.replaceChildren();
      setStatus();
      dialogLink.removeAttribute('href');
      if (dialog.open) dialog.close();
      state.dialogOpen = false;
      restoreOverflow();
      state.origin?.focus({ preventScroll: true });
      if (restart) resumeAfter();
    };
    const updateFocus = () => window.setTimeout(() => {
      state.focused = strip.contains(document.activeElement);
      if (state.focused) stopAnimation();
      else startAnimation();
    });
    const visibilityChangeHandler = () => {
      if (document.hidden) stopAnimation();
      else startAnimation();
    };
    const touchFinishedHandler = () => {
      state.touching = false;
      startAnimation();
    };
    const resizeHandler = () => {
      measureLoop();
      normalizeScroll();
    };
    const updateToggle = () => {
      toggle.textContent = state.userPaused ? 'Play' : 'Pause';
      toggle.setAttribute('aria-pressed', state.userPaused ? 'true' : 'false');
    };
    const toggleHandler = () => {
      state.userPaused = !state.userPaused;
      updateToggle();
      if (state.userPaused) stopAnimation();
      else startAnimation();
    };
    const visualClickHandler = (event) => {
      const proxy = event.target.closest('[data-craft-proxy-index]');
      if (!proxy) return;
      const proxyLink = event.target.closest('[data-craft-proxy-link]');
      if (proxyLink?.dataset.craftProxyLink) {
        window.location.assign(proxyLink.dataset.craftProxyLink);
        return;
      }
      const item = items[Number(proxy.dataset.craftProxyIndex)];
      const card = item?.querySelector('[data-craft-card]');
      if (card) openCard(card);
    };
    const isOutsideDialog = (event) => {
      if (event.target !== dialog) return false;
      const bounds = dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
    };
    const dialogPointerHandler = (event) => {
      if (event.type === 'pointerdown') {
        state.backdropPointerStarted = isOutsideDialog(event);
        state.backdropPointerCompleted = false;
        return;
      }
      state.backdropPointerCompleted = state.backdropPointerStarted && isOutsideDialog(event);
    };
    const dialogClickHandler = (event) => {
      if (isOutsideDialog(event) && (state.backdropPointerCompleted || event.detail === 0)) closeDialog();
      state.backdropPointerStarted = false;
      state.backdropPointerCompleted = false;
    };
    const dialogPointerCancelHandler = () => {
      state.backdropPointerStarted = false;
      state.backdropPointerCompleted = false;
    };

    cards.forEach((card) => card.addEventListener('click', () => openCard(card)));
    visualTrack.addEventListener('click', visualClickHandler);
    strip.addEventListener('pointerenter', () => { state.hovered = true; stopAnimation(); });
    strip.addEventListener('pointerleave', () => { state.hovered = false; startAnimation(); });
    strip.addEventListener('focusin', updateFocus);
    strip.addEventListener('focusout', updateFocus);
    strip.addEventListener('touchstart', () => { state.touching = true; stopAnimation(); }, { passive: true });
    strip.addEventListener('touchend', touchFinishedHandler, { passive: true });
    strip.addEventListener('touchcancel', touchFinishedHandler, { passive: true });
    document.addEventListener('visibilitychange', visibilityChangeHandler);
    window.addEventListener('resize', resizeHandler);
    previous?.addEventListener('click', () => stepBy(-1));
    toggle?.addEventListener('click', toggleHandler);
    next?.addEventListener('click', () => stepBy(1));
    closeButton?.addEventListener('click', closeDialog);
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeDialog(); });
    dialog.addEventListener('pointerdown', dialogPointerHandler);
    dialog.addEventListener('pointerup', dialogPointerHandler);
    dialog.addEventListener('pointercancel', dialogPointerCancelHandler);
    dialog.addEventListener('click', dialogClickHandler);

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(([entry]) => {
        state.intersecting = Boolean(entry?.isIntersecting);
        if (state.intersecting) startAnimation();
        else stopAnimation();
      });
      state.observer.observe(strip);
    }

    craftedInTibetControllers.set(strip, {
      destroy() {
        stopAnimation();
        clearResumeTimer();
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
        window.removeEventListener('resize', resizeHandler);
        visualTrack.removeEventListener('click', visualClickHandler);
        state.observer?.disconnect();
        state.observer = null;
        if (state.dialogOpen || dialog.open) closeDialog(false);
        else restoreOverflow();
        strip.dataset.craftReady = '';
      }
    });
    measureLoop();
    normalizeScroll();
    updateToggle();
    startAnimation();
  });
}

initCraftedInTibet(document);
document.addEventListener('shopify:section:load', (event) => initCraftedInTibet(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-pk-crafted-strip]').forEach((strip) => craftedInTibetControllers.get(strip)?.destroy());
});
